import { useEffect, useRef } from "react";
import * as THREE from "three";

export const REGION_META = {
  prefrontal_cortex: { pos: [0, 0.42, 1.0] },
  amygdala: { pos: [0.42, -0.22, 0.3] },
  nucleus_accumbens: { pos: [-0.18, -0.12, 0.72] },
  hippocampus: { pos: [-0.52, -0.18, -0.05] },
  visual_cortex: { pos: [0, 0.18, -1.08] },
  auditory_cortex: { pos: [0.82, 0.02, 0.05] },
  insula: { pos: [-0.72, 0.08, 0.3] },
  anterior_cingulate: { pos: [0, 0.6, 0.45] },
  mirror_neurons: { pos: [0.66, 0.5, -0.35] },
};

function makeBrainGeometry(count = 7000) {
  const positions = new Float32Array(count * 3);
  const colors = new Float32Array(count * 3);
  const cream = new THREE.Color("#F3F0E9");
  const gold = new THREE.Color("#C8A24C");
  const cortexCount = Math.floor(count * 0.86);
  let i = 0;
  while (i < count) {
    let x, y, z;
    if (i < cortexCount) {
      const u = Math.random() * 2 - 1;
      const t = Math.random() * Math.PI * 2;
      const s = Math.sqrt(1 - u * u);
      x = s * Math.cos(t); y = u; z = s * Math.sin(t);
      const r = 0.9 + Math.random() * 0.12;
      x *= r; y *= r; z *= r;
      const w = 0.05 * Math.sin(x * 9 + z * 4) * Math.sin(y * 11) + 0.04 * Math.sin(z * 8 - y * 5 + x * 3);
      x += x * w; y += y * w; z += z * w;
      x *= 0.92; y *= 0.78; z *= 1.18;
      if (y < -0.34) y = -0.34 + (y + 0.34) * 0.42;
      if (Math.abs(x) < 0.03 && y > 0) continue;
      x += Math.sign(x) * 0.03;
    } else {
      const u = Math.random() * 2 - 1;
      const t = Math.random() * Math.PI * 2;
      const s = Math.sqrt(1 - u * u);
      const r = 0.85 + Math.random() * 0.15;
      x = s * Math.cos(t) * 0.42 * r;
      y = u * 0.26 * r - 0.44;
      z = s * Math.sin(t) * 0.34 * r - 0.82;
    }
    positions[i * 3] = x;
    positions[i * 3 + 1] = y;
    positions[i * 3 + 2] = z;
    const c = Math.random() < 0.14 ? gold : cream;
    colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
    i += 1;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  return geo;
}

export default function BrainScene({ activations = [], analyzing = false }) {
  const mountRef = useRef(null);
  const labelsRef = useRef(null);
  const apiRef = useRef(null);
  const propsRef = useRef({ analyzing, hasResult: false });
  propsRef.current = { analyzing, hasResult: activations.length > 0 };

  useEffect(() => {
    const mount = mountRef.current;
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, mount.clientWidth / mount.clientHeight, 0.1, 50);
    camera.position.set(0, 0.25, 3.05);
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.domElement.style.cssText = "position:absolute;inset:0;";
    mount.appendChild(renderer.domElement);

    const group = new THREE.Group();
    group.rotation.set(0.12, 0.6, 0);
    scene.add(group);

    const points = new THREE.Points(
      makeBrainGeometry(),
      new THREE.PointsMaterial({
        vertexColors: true,
        size: 0.018,
        sizeAttenuation: true,
        transparent: true,
        opacity: 0.8,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      })
    );
    group.add(points);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.15, 0.004, 8, 80),
      new THREE.MeshBasicMaterial({ color: "#C8A24C", transparent: true, opacity: 0.8, blending: THREE.AdditiveBlending })
    );
    ring.rotation.x = Math.PI / 2;
    ring.visible = false;
    group.add(ring);

    const markerGroup = new THREE.Group();
    group.add(markerGroup);
    apiRef.current = { markerGroup };

    const clock = new THREE.Clock();
    const v = new THREE.Vector3();
    let raf;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const { analyzing: an, hasResult } = propsRef.current;
      const dt = clock.getDelta();
      const t = clock.elapsedTime;
      group.rotation.y += dt * (an ? 0.65 : hasResult ? 0.06 : 0.16);
      ring.visible = an;
      if (an) ring.position.y = Math.sin(t * 1.6) * 0.75;

      const labels = labelsRef.current ? labelsRef.current.children : [];
      markerGroup.children.forEach((m, idx) => {
        const p = 1 + 0.25 * Math.sin(t * 2.4 + idx * 1.7);
        if (m.children[0]) m.children[0].scale.setScalar(p);
        if (m.children[1]) m.children[1].scale.setScalar(p * 1.9);
        const el = labels[idx];
        if (el) {
          m.getWorldPosition(v);
          v.project(camera);
          const x = (v.x * 0.5 + 0.5) * mount.clientWidth;
          const y = (-v.y * 0.5 + 0.5) * mount.clientHeight;
          el.style.transform = `translate(-50%, -100%) translate(${x}px, ${y - 12}px)`;
          el.style.opacity = v.z < 1 ? "1" : "0";
        }
      });
      renderer.render(scene, camera);
    };
    animate();

    const onResize = () => {
      const w = mount.clientWidth, h = mount.clientHeight;
      if (!w || !h) return;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
      apiRef.current = null;
    };
  }, []);

  useEffect(() => {
    const api = apiRef.current;
    const labelBox = labelsRef.current;
    if (!api || !labelBox) return;
    api.markerGroup.clear();
    labelBox.innerHTML = "";
    activations.forEach((a) => {
      const meta = REGION_META[a.id];
      if (!meta) return;
      const base = 0.045 + (a.activation / 100) * 0.05;
      const holder = new THREE.Group();
      holder.position.set(meta.pos[0], meta.pos[1], meta.pos[2]);
      const core = new THREE.Mesh(
        new THREE.SphereGeometry(base, 16, 16),
        new THREE.MeshBasicMaterial({ color: "#C8A24C", transparent: true, opacity: 0.95 })
      );
      const halo = new THREE.Mesh(
        new THREE.SphereGeometry(base, 16, 16),
        new THREE.MeshBasicMaterial({ color: "#C8A24C", transparent: true, opacity: 0.14, blending: THREE.AdditiveBlending, depthWrite: false })
      );
      holder.add(core, halo);
      api.markerGroup.add(holder);

      const el = document.createElement("div");
      el.style.cssText = "position:absolute;top:0;left:0;text-align:center;transition:opacity .3s;will-change:transform;";
      el.innerHTML = `<div style="font-family:'Outfit',sans-serif;text-transform:uppercase;letter-spacing:.22em;font-size:9px;color:#F3F0E9;opacity:.9;white-space:nowrap">${a.name}</div><div style="font-family:'Bebas Neue',sans-serif;font-size:14px;color:#C8A24C">${a.activation}%</div>`;
      labelBox.appendChild(el);
    });
  }, [activations]);

  return (
    <div ref={mountRef} data-testid="brain-scene" className="relative w-full h-full overflow-hidden">
      <div ref={labelsRef} className="absolute inset-0 pointer-events-none z-10" />
    </div>
  );
}
